const partsFormatter = (timeZone: string) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

const readPart = (parts: Intl.DateTimeFormatPart[], type: string) =>
  Number(parts.find((part) => part.type === type)?.value ?? 0);

// An unknown zone name, or an engine whose Intl lacks formatToParts, must degrade to device time.
const safeFormatToParts = (date: Date, timeZone: string) => {
  try {
    return partsFormatter(timeZone).formatToParts(date);
  } catch {
    return null;
  }
};

// Local components carrying `timeZone`'s wall clock, so a local-rendering grid paints that zone.
export const toZonedTime = (date: Date, timeZone: string): Date => {
  const parts = safeFormatToParts(date, timeZone);
  if (!parts) {
    return date;
  }

  return new Date(
    readPart(parts, 'year'),
    readPart(parts, 'month') - 1,
    readPart(parts, 'day'),
    readPart(parts, 'hour'),
    readPart(parts, 'minute'),
    readPart(parts, 'second'),
    date.getMilliseconds(),
  );
};

// Inverse of toZonedTime, solved twice: one pass lands an hour out across a DST boundary.
export const fromZonedTime = (zonedDate: Date, timeZone: string): Date => {
  const offsetAt = (candidate: Date) =>
    toZonedTime(candidate, timeZone).getTime() - candidate.getTime();

  const firstPass = new Date(zonedDate.getTime() - offsetAt(zonedDate));

  return new Date(zonedDate.getTime() - offsetAt(firstPass));
};
